import {
    CONSTANTS,
    customMinInvestmentSteps,
    FILTER_OPTIONS,
    OPPORTUNITY_BOND_TYPES,
    VALID_AMOUNT_VALUES,
    VALID_RATING_FILTERS,
    VALID_TENURE_FILTERS,
    VALID_YIELD_FILTERS,
} from './constants';
import {
    getNumberWithComma,
    triggerCustomGaEvent,
} from '@helpers/miscelleanous';
import { opportunitiesGaEventsName } from '@helpers/ga-events-name-enum';
import { opportunityClosedStates } from '@helpers/constants';

export const getRatingHelperText = (selectedRatings) => {
    if (selectedRatings.length === 0) {
        return false;
    }

    const ratingRanges = {
        high: 'AAA to AA-',
        medium: 'A+ to BBB+',
        low: 'BBB to D',
    };

    if (selectedRatings.length === 1) {
        const rating = selectedRatings[0];
        if (rating === 'high') {
            return 'Ratings from AAA to AA- are categorized as High rated.';
        }
        if (rating === 'medium') {
            return 'Ratings from A+ to BBB+ are categorized as Medium rated.';
        }
        if (rating === 'low') {
            return 'Ratings from BBB to D are categorized as Low rated.';
        }
    }

    if (selectedRatings.length > 1) {
        const sortedRatings = selectedRatings.sort((a, b) => {
            const order = ['low', 'medium', 'high'];
            return order.indexOf(a) - order.indexOf(b);
        });

        const minRange = ratingRanges[sortedRatings[0]];
        const maxRange = ratingRanges[sortedRatings[sortedRatings.length - 1]];

        if (selectedRatings[0] === 'low' && selectedRatings[1] === 'high') {
            return 'Bonds rated from AAA to AA- & BBB to D fall under the selected categories.';
        }

        return `Bonds rated from ${maxRange.split(' ')[0]} to ${
            minRange.split(' ')[2]
        } fall under the selected categories.`;
    }

    return false;
};

export const getShortNumber = (num) => {
    if(!num) return null;
    if (num >= 10000000) {
        return (num / 10000000).toFixed(0) + 'Cr';
    } else if (num >= 100000) {
        return (num / 100000).toFixed(0) + 'Lakh';
    } else if (num >= 1000) {
        return (num / 1000).toFixed(0) + 'k';
    }
    return num.toString();
};

export const labelFormatter = (
    value,
    isValueAtEnd = false,
    isExtremeEnd = false
) => {
    if (value === 0) return isExtremeEnd ? '' : '₹0';

    if (value === 10000) return isValueAtEnd ? '< ~ ₹10k' : '~ ₹10k';
    const lastSecondStep = customMinInvestmentSteps.at(-2);
    if (value > lastSecondStep || isValueAtEnd) return '~ ₹1Lakh +';
    if (value === lastSecondStep) return '~ ₹1Lakh';
    return `${value > 0 ? '~ ': ''}₹${getShortNumber(value)}`;
};

export const parseQueryValue = (value) => (value ? value.split(',') : []);
export const parseAmount = (amount) => {
    if (!amount) return CONSTANTS.MIN_MAX_AMOUNT;

    if (amount.includes('-')) {
        // Format like "25000-75000"
        return amount.split('-').map((item) => parseInt(item, 10));
    } else if (amount.startsWith('>=')) {
        //For  format like ">=25000"
        const min = parseInt(amount.replace('>=', ''), 10);
        return [min, CONSTANTS.MIN_MAX_AMOUNT[1]];
    }

    return CONSTANTS.MIN_MAX_AMOUNT;
};

export const getAppliedFilters = (queryParams) => {
    const chips = [];
    let sortFiled = {};
    let isSorted = false;
    
    const {validatedFilters} = validateFilters(queryParams);
    queryParams = validatedFilters;

    // Yield
    if (queryParams.yield) {
        const yieldValues = queryParams.yield.split(',');
        yieldValues.forEach((yieldValue) => {
            const value = FILTER_OPTIONS.yield.find(
                (option) => option.value === yieldValue
            );

            chips.push({
                key: 'yield',
                label: `Yield: ${value?.label}`,
                value: yieldValue,
            });
        });
    }

    // Tenure
    if (queryParams.tenure) {
        const tenureValues = queryParams.tenure.split(',');
        tenureValues.forEach((tenureValue) => {
            const value = FILTER_OPTIONS.tenure.find(
                (option) => option.value === tenureValue
            );
            chips.push({
                key: 'tenure',
                label: `Tenure: ${value?.label}`,
                value: tenureValue,
            });
        });
    }

    // Min Investment
    if (queryParams.amount) {
        let min, max, formattedMin, formattedMax;

        if (queryParams.amount.includes('-')) {
            // Handle when `amount` is a range in string format (e.g., "0-5000")
            [min, max] = queryParams.amount.split('-').map(Number);
        } else if (queryParams.amount.startsWith('>=') || queryParams.amount.startsWith('>')) {
            // Handle when `amount` is a single minimum value (e.g., ">=1000" or ">1000")
            min = Math.min(Number(queryParams.amount.replace(/>=?|>/, '')), customMinInvestmentSteps.at(-2));
            max = null; // No maximum
        } else if (queryParams.amount.startsWith('<=') || queryParams.amount.startsWith('<')) {
            // Handle when `amount` is a single maximum value (e.g., "<=5000" or "<5000")
            min = null; // No minimum
            max = Number(queryParams.amount.replace(/<=?|</, ''));
        }
        formattedMin = min !== undefined ? `${labelFormatter(min)}` : '';
        formattedMax =
            max === null
                ? ''
                : `${labelFormatter(max)}`;

        const label = (() => {
            if (min === 0 && max !== null) return  `Min Investment: Upto ${formattedMax}`;
            if (min === null) return `Min Investment: < ${formattedMax}`;
            if (max === null) return `Min Investment: > ${formattedMin}`;
        
            return `Min Investment: ${formattedMin} to ${formattedMax}`;
        })();

        chips.push({
            key: 'amount',
            label: label,
            value: queryParams.amount,
        });
    }

    // Rating
    if (queryParams.rating) {
        const ratingValues = queryParams.rating.split(',');

        ratingValues.forEach((ratingValue) => {
            const value = FILTER_OPTIONS.rating.find(
                (option) => option.value === ratingValue
            );
            chips.push({
                key: 'rating',
                label: `Rating: ${value.label}`,
                value: ratingValue,
            });
        });
    }

    // Secured Opportunities
    if (queryParams.secured === 'true') {
        chips.push({
            label: 'Secured',
            value: queryParams.secured,
            key: 'secured',
        });
    }

    if (queryParams.sort || queryParams.sortBy) {
        sortFiled = {
            sortOrder: queryParams.sort,
            sortBy: queryParams.sortBy,
        };
        isSorted = true;
    }

    return { chips, count: chips.length, sortFiled, isSorted };
};

export const isBondsView = (type) => type === OPPORTUNITY_BOND_TYPES.BOND;

export const sdiTabs = {
    RBI_REGULATED: 'RBI Regulated',
    SEBI_REGULATED: 'SEBI Regulated',
};

export const triggerGaEventsForsdiTabs = (value, showActiveOpportunities) => {
    let eventName;
    if (showActiveOpportunities) {
        switch (value) {
            case sdiTabs.RBI_REGULATED:
                eventName =
                    opportunitiesGaEventsName.CLICKED_RBI_SDI_TAB_ACTIVE;
                break;
            case sdiTabs.SEBI_REGULATED:
                eventName =
                    opportunitiesGaEventsName.CLICKED_SEBI_SDI_TAB_ACTIVE;
                break;
            default:
                break;
        }
    } else {
        switch (value) {
            case sdiTabs.RBI_REGULATED:
                eventName =
                    opportunitiesGaEventsName.CLICKED_RBI_SDI_TAB_RECENTLY_CLOSED;
                break;
            case sdiTabs.SEBI_REGULATED:
                eventName =
                    opportunitiesGaEventsName.CLICKED_SEBI_SDI_TAB_RECENTLY_CLOSED;
                break;
            default:
                break;
        }
    }
    triggerCustomGaEvent(eventName);
};

export const getRegulationTabs = (stats, showActiveOpportunities) => {
    const initialTabs = [
        {
            id: 'rbi',
            value: 'RBI Regulated',
            count: stats?.rbiSDIs || 0,
            onClick: () => {
                triggerGaEventsForsdiTabs(
                    sdiTabs.RBI_REGULATED,
                    showActiveOpportunities
                );
            },
        },
        {
            id: 'sebi',
            value: 'SEBI Regulated',
            count: stats?.sebiSDIs || 0,
            onClick: () => {
                triggerGaEventsForsdiTabs(
                    sdiTabs.SEBI_REGULATED,
                    showActiveOpportunities
                );
            },
        },
    ];

    return !initialTabs[0].count && initialTabs[1].count
        ? [initialTabs[1], initialTabs[0]]
        : initialTabs;
};

export const isTBillsView = (type) =>
    type === OPPORTUNITY_BOND_TYPES.TreasuryBill;

export const isAdminDeal = (isAuthorized, opportunity) =>
    isAuthorized &&
    opportunity?.accessCode === process.env.NEXT_PUBLIC_ADMIN_ACCESS_CODE &&
    !opportunityClosedStates.includes(opportunity.state);

export const isFestiveDeal = (
    isAuthorized,
    showFestiveDays,
    opportunity,
    isBonds
) =>
    isAuthorized &&
    showFestiveDays?.isActive &&
    opportunity?.opportunityPreFestiveOfferIRR &&
    isBonds;

export const getCachedData = (isAuthorized, showActiveOpportunities) => {
    const activeCountKey = 'activeOpportunitiesCount';
    const closedCountKey = 'closedOpportunitiesCount';
    const authActiveCountKey = 'authActiveOpportunitiesCount';
    const authClosedCountKey = 'authClosedOpportunitiesCount';

    const storageKey = showActiveOpportunities
        ? isAuthorized
            ? authActiveCountKey
            : activeCountKey
        : isAuthorized
        ? authClosedCountKey
        : closedCountKey;
    const storedData = sessionStorage.getItem(storageKey);
    const cachedData = storedData ? JSON.parse(storedData) : null;
    return { cachedData, storageKey };
};


const findPreviousValidAmount = (num) => {
    /* Reversing ensures we check from the largest to smallest valid amount.
    This mkes sure that we get the nearest valid lower value.
    Without reversing or by changing the operator, iw would return the first match
    from the left (smallest valid number) which may not always be the closest lower value
    For example, if num = 24000, it should return 10000, not 0 or 25000
    */
    return [...VALID_AMOUNT_VALUES].reverse().find(v => v <= num) || VALID_AMOUNT_VALUES[0];
};

const findNextValidAmount = (num) => {
    return VALID_AMOUNT_VALUES.find(v => v >= num) || VALID_AMOUNT_VALUES[VALID_AMOUNT_VALUES.length - 1];
};

export const validateAmountRange = (amount) => {
    if (!amount) return false;

    if (amount.startsWith('>=')) {
        let min = parseInt(amount.slice(2), 10);
        min = amount.match(/\d+/g)?.join('') || 0;
        min = VALID_AMOUNT_VALUES.includes(min) ? min : findPreviousValidAmount(min);
        return `>=${min}`;
    }

    let [min, max] = amount.split('-').filter(v => v !== "").map(Number);

    if (!isNaN(min) && !isNaN(max)) {
        if(max > 100000){
            min = VALID_AMOUNT_VALUES.includes(min) ? min : findPreviousValidAmount(min);
            return `>=${min}`
        }
        if(min === max){
            min = VALID_AMOUNT_VALUES.includes(min) ? min : findPreviousValidAmount(min);
            max = findNextValidAmount(max+1);  
        }
        else { 
            min = VALID_AMOUNT_VALUES.includes(min) ? min : findPreviousValidAmount(min);
            max = VALID_AMOUNT_VALUES.includes(max) ? max : findNextValidAmount(max);  
        }      
        return min < max ? `${min}-${max}` : false;
    }

    return false;
};


export const validateFilters = (filters) => {
    let validatedFilters = { ...filters };
    let hasChanges = false;
    Object.entries(validatedFilters).forEach(([key, value]) => {
        if (typeof value !== 'string') return;
        const values = value.split(',').map(v => v.trim());
        switch (key) {
            case 'amount':
                const newAmount = validateAmountRange(value);
                if (newAmount !== value) {
                    if (newAmount) validatedFilters[key] = newAmount;
                    else delete validatedFilters[key];
                    hasChanges = true;
                }
                break;
            case 'yield':
                const validYieldValues = values.filter(v => VALID_YIELD_FILTERS.includes(v));
                if (validYieldValues.length !== values.length) {
                    if (validYieldValues.length > 0) validatedFilters[key] = validYieldValues.join(',');
                    else delete validatedFilters[key];
                    hasChanges = true;
                }
                break;
            case 'rating':
                const validRatingValues = values.filter(v => VALID_RATING_FILTERS.includes(v));
                if (validRatingValues.length !== values.length) {
                    if (validRatingValues.length > 0) validatedFilters[key] = validRatingValues.join(',');
                    else delete validatedFilters[key];
                    hasChanges = true;
                }
                break;
            case 'secured':
                if (value !== 'true') {
                    delete validatedFilters[key];
                    hasChanges = true;
                }
                break;
            case 'tenure':
                const validTenureValues = values.filter(v => VALID_TENURE_FILTERS.includes(v));
                if (validTenureValues.length !== values.length) {
                    if (validTenureValues.length > 0) validatedFilters[key] = validTenureValues.join(',');
                    else delete validatedFilters[key];
                    hasChanges = true;
                }
                break;
            default:
                break; // If key is not recognized, consider it valid
        }
    }); 
    if(hasChanges && validatedFilters.quickFilters) delete validatedFilters.quickFilters;
    
    return {validatedFilters , hasChanges};
};